import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import type { NewsBookmark } from '../../utils/newsBookmarks';

interface NewsBookmarkListProps {
  bookmarks: NewsBookmark[];
  onRemove: (articleId: number) => void;
  /** 他のタブと同じくクッションを挟む。ここだけ外部へ直行すると挙動が食い違う */
  onOpenDetail: (articleId: number) => void;
}

// NewsFeed と同じグリッド。タブを切り替えてもカードの寸法が変わらないようにする
const GRID = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  gap: { xs: 1.25, sm: 1.75 },
} as const;

/**
 * ブックマーク一覧。
 *
 * サーバーではなく localStorage のスナップショットを表示する。
 * サーバー側の記事は保持期間で消えるが、ここは残り続けるのが狙い。
 * ただし画像は媒体側のURLなので、時間が経つと表示できなくなることがある
 * （その場合はプレースホルダに落とす）。
 */
const NewsBookmarkList: React.FC<NewsBookmarkListProps> = ({
  bookmarks, onRemove, onOpenDetail,
}) => {
  const [failed, setFailed] = React.useState<Set<number>>(new Set());

  if (bookmarks.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
          保存した記事はまだありません
        </Typography>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
            color: 'rgba(255,255,255,0.3)', fontSize: { xs: '0.55rem', sm: '0.6rem' }, mt: 0.5,
          }}
        >
          カードの
          <BookmarkBorderIcon sx={{ fontSize: 14 }} />
          から保存できます（この端末にのみ残ります）
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={GRID}>
      {bookmarks.map((b) => {
        const showImage = b.image_url && !failed.has(b.article_id);
        return (
          <Box
            key={b.article_id}
            onClick={() => onOpenDetail(b.article_id)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'rgba(255,255,255,0.035)',
              contentVisibility: 'auto',
              containIntrinsicSize: 'auto 260px',
              border: '1px solid rgba(255,255,255,0.07)',
              transition: 'border-color .18s ease, background-color .18s ease',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.16)',
                bgcolor: 'rgba(255,255,255,0.055)',
              },
              '&:hover .news-thumb': { transform: 'scale(1.04)' },
            }}
          >
            <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', overflow: 'hidden' }}>
              {showImage ? (
                <Box
                  component="img"
                  className="news-thumb"
                  src={b.image_url!}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={() => setFailed((prev) => new Set(prev).add(b.article_id))}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    transition: 'transform .4s ease',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: 'rgba(255,255,255,0.18)',
                  }}
                >
                  <NewspaperIcon sx={{ fontSize: 34 }} />
                </Box>
              )}

              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.15) 34%, rgba(0,0,0,0) 58%)',
                  pointerEvents: 'none',
                }}
              />

              <Typography
                sx={{
                  position: 'absolute',
                  left: 9,
                  bottom: 7,
                  maxWidth: 'calc(100% - 18px)',
                  fontSize: { xs: '0.5rem', sm: '0.55rem' },
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.8)',
                  textShadow: '0 1px 3px rgba(0,0,0,0.7)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {b.source_name}
              </Typography>

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(b.article_id);
                }}
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  width: 28,
                  height: 28,
                  bgcolor: 'rgba(0,0,0,0.45)',
                  backdropFilter: 'blur(6px)',
                  WebkitBackdropFilter: 'blur(6px)',
                  color: 'rgba(255,255,255,0.75)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.65)', color: '#ff8a80' },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Typography
                sx={{
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  fontWeight: 600,
                  lineHeight: 1.45,
                  color: 'rgba(255,255,255,0.92)',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {b.title}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ mt: 1, fontSize: { xs: '0.5rem', sm: '0.55rem' }, color: 'rgba(255,255,255,0.4)' }}>
                {new Date(b.saved_at).toLocaleDateString('ja-JP')} 保存
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default NewsBookmarkList;
